import {
  authApi,
  type AuthResponse,
  type GenericErrorResponse,
} from "./authService";

export interface SavingsContextItemSchema {
  columns: Array<{
    id: number;
    label: string;
    description?: string;
    is_editable: boolean;
    is_required: boolean;
    constraints?: {
      type: string;
      min_value?: number;
      max_value?: number;
      allowed_values?: string[];
      numeric_values?: number[];
      step_value?: number;
    };
  }>;
  rows: Array<{
    id: number;
    label: string;
    label2?: string;
    description?: string;
    is_required: boolean;
    non_editable_columns?: number[];
  }> | null;
}

export interface SavingsFieldMapEntry {
  field_key: string;
  legacy_column_id: number;
  legacy_row_id: number | null;
}

/** Shape kept in component state for the currently-open savings category. */
export interface SavingsContextItem {
  assessment_type: string;
  submission_id: number | null;
  submission_state: string | null;
  schema: SavingsContextItemSchema;
  field_map: SavingsFieldMapEntry[];
}

interface SchemaResponse {
  assessment_type: string;
  category: string;
  schema_version: number;
  schema: SavingsContextItemSchema;
  field_map: SavingsFieldMapEntry[];
}

interface ValuesResponseItem {
  field_key: string;
  row_key: string | null;
  value: string | number | boolean | null;
}

interface ValuesResponse {
  submission_id: number;
  state: string;
  values: ValuesResponseItem[];
}

export interface AvailableResponseItem {
  assessment_type: string;
  category: string;
  description: string;
  baseline_submission_id: number | null;
  baseline_submission_state: string | null;
  baseline_result_available: boolean;
  savings_submission_id: number | null;
  savings_submission_state: string | null;
  savings_result_available: boolean;
}

/**
 * Which certification step the lab may work on. The backend enforces this order
 * on every write, so the UI reads it instead of inferring it.
 */
export interface AvailableWorkflow {
  checklist_unlocked: boolean;
  checklist_locked_reason: string | null;
  savings_unlocked: boolean;
  savings_locked_reason: string | null;
}

export interface AvailableResponse {
  lab_id: string;
  level: string;
  baseline_progression_enabled?: boolean;
  workflow: AvailableWorkflow;
  items: AvailableResponseItem[];
}

export interface SubmissionResultResponse {
  submission_id: number;
  state: string;
  submission_type: string;
  result: Record<string, unknown>;
  result_schema_version: number | null;
  locked_at: string | null;
  locked_by: string | null;
}

export interface DeltaResultResponse {
  delta_result: Record<string, unknown>;
}

export class AssessmentService {
  /** List available assessments with per-category submission states. */
  static async getAvailable(labId: string, level?: string): Promise<AvailableResponse> {
    const url = level ? `/assessment/${labId}/available?level=${level}` : `/assessment/${labId}/available`;
    const response = (await authApi(
      url,
    )) as (AuthResponse & AvailableResponse) | GenericErrorResponse;

    if (!response.success) {
      throw new Error(
        (response as GenericErrorResponse).error ??
          "Failed to fetch available assessments",
      );
    }

    return response as unknown as AvailableResponse;
  }

  /** Return the legacy form schema + field_map for one assessment type. */
  static async getSchema(
    labId: string,
    assessmentType: string,
    level: string,
  ): Promise<SchemaResponse> {
    const response = (await authApi(
      `/assessment/${labId}/${assessmentType.toLowerCase()}/schema?level=${level}`,
    )) as (AuthResponse & SchemaResponse) | GenericErrorResponse;

    if (!response.success) {
      throw new Error(
        (response as GenericErrorResponse).error ?? "Failed to fetch schema",
      );
    }

    return response as unknown as SchemaResponse;
  }

  /** Return all non-deleted saved values for a submission. */
  static async getValues(
    labId: string,
    assessmentType: string,
    submissionId: number,
  ): Promise<ValuesResponse> {
    const response = (await authApi(
      `/assessment/${labId}/${assessmentType.toLowerCase()}/submissions/${submissionId}/values`,
    )) as (AuthResponse & ValuesResponse) | GenericErrorResponse;

    if (!response.success) {
      throw new Error(
        (response as GenericErrorResponse).error ??
          "Failed to fetch submission values",
      );
    }

    return response as unknown as ValuesResponse;
  }

  /** Return the computed result for a previously locked submission. */
  static async getSubmissionResult(
    submissionId: number,
  ): Promise<SubmissionResultResponse> {
    const response = (await authApi(
      `/assessment/submissions/${submissionId}/result`,
    )) as (AuthResponse & SubmissionResultResponse) | GenericErrorResponse;

    if (!response.success) {
      throw new Error(
        (response as GenericErrorResponse).error ??
          "Failed to fetch submission result",
      );
    }

    return response as unknown as SubmissionResultResponse;
  }

  /** Return the computed savings delta for the current assessment cycle. */
  static async getDeltaResult(
    labId: string,
    assessmentType: string,
    level: string,
  ): Promise<DeltaResultResponse> {
    // The level is required. A lab can hold more than one certification, so
    // without it the backend has no correct answer and returns 400.
    const response = (await authApi(
      `/assessment/${labId}/${assessmentType.toLowerCase()}/delta-result?level=${level}`,
    )) as (AuthResponse & DeltaResultResponse) | GenericErrorResponse;

    if (!response.success) {
      throw new Error(
        (response as GenericErrorResponse).error ??
          "Failed to fetch assessment delta result",
      );
    }

    return response as unknown as DeltaResultResponse;
  }

  /** Save a single After-column field value. */
  static async saveSavingsValue(
    labId: string,
    assessmentType: string,
    submissionId: number,
    fieldKey: string,
    value: unknown,
    rowKey?: string,
  ): Promise<void> {
    const body: Record<string, unknown> = { field_key: fieldKey, value };
    if (rowKey !== undefined) {
      body.row_key = rowKey;
    }

    const response = (await authApi(
      `/assessment/${labId}/${assessmentType.toLowerCase()}/submissions/${submissionId}/save`,
      { method: "POST", body: JSON.stringify(body) },
    )) as (AuthResponse & { saved?: boolean }) | GenericErrorResponse;

    if (!response.success) {
      throw new Error(
        (response as GenericErrorResponse).error ?? "Failed to save value",
      );
    }
  }

  /** Delete all After-column values for a dynamic row. */
  static async deleteSavingsRow(
    labId: string,
    assessmentType: string,
    submissionId: number,
    rowKey: string,
  ): Promise<{ deleted_count: number }> {
    const response = (await authApi(
      `/assessment/${labId}/${assessmentType.toLowerCase()}/submissions/${submissionId}/rows/${rowKey}`,
      { method: "DELETE" },
    )) as (AuthResponse & { deleted_count?: number }) | GenericErrorResponse;

    if (!response.success) {
      throw new Error(
        (response as GenericErrorResponse).error ?? "Failed to delete row",
      );
    }

    return {
      deleted_count:
        (response as AuthResponse & { deleted_count?: number }).deleted_count ??
        0,
    };
  }

  /** Lock (submit) a savings submission for a category. */
  static async submitSavingsAssessment(
    labId: string,
    assessmentType: string,
    submissionId: number,
  ): Promise<{
    submission_id: number;
    state: string;
    result: Record<string, unknown>;
  }> {
    const response = (await authApi(
      `/assessment/${labId}/${assessmentType.toLowerCase()}/submissions/${submissionId}/submit`,
      { method: "POST" },
    )) as
      | (AuthResponse & {
          submission_id?: number;
          state?: string;
          result?: Record<string, unknown>;
        })
      | GenericErrorResponse;

    if (!response.success) {
      throw new Error(
        (response as GenericErrorResponse).error ??
          "Failed to submit savings assessment",
      );
    }

    return {
      submission_id:
        (response as AuthResponse & { submission_id?: number }).submission_id ??
        submissionId,
      state: (response as AuthResponse & { state?: string }).state ?? "LOCKED",
      result:
        (response as AuthResponse & { result?: Record<string, unknown> }).result ??
        {},
    };
  }
}
